import * as styles from "./BalanceSheetPanel.css";

import { useAtom, useSetAtom } from "jotai";
import type { JSX, ReactNode } from "react";
import { useEffect } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Input } from "@frontend/components/atoms/Input";
import { Spinner } from "@frontend/components/atoms/Spinner";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { formatAmount, parseAmount } from "@frontend/utils/amount";
import { formatMinutes } from "@frontend/utils/duration";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  applyBalanceSheetAtom,
  balanceSheetAtom,
  loadBalanceSheetAtom,
} from "../stores/balanceSheet";
import { Section } from "./Section";

export type BalanceSheetPanelProps = {
  projectId: number;
};

export function BalanceSheetPanel({
  projectId,
}: BalanceSheetPanelProps): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const [state, setState] = useAtom(balanceSheetAtom);
  const load = useSetAtom(loadBalanceSheetAtom);
  const apply = useSetAtom(applyBalanceSheetAtom);

  useEffect(() => {
    void load(projectId);
  }, [load, projectId]);

  const { sheet, loading, saving, error } = state;

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner aria-label={t`Loading`} />
      </div>
    );
  }

  if (null === sheet) {
    return (
      <p className={styles.alert} role="alert">
        {error ?? <Trans>This balance sheet could not be read.</Trans>}
      </p>
    );
  }

  const money = (value: string | null): string =>
    null === value
      ? "-"
      : `${formatAmount(parseAmount(value) ?? 0, locale)} CHF`;

  const gain = parseAmount(sheet.bill?.gain ?? null);

  return (
    <div className={styles.panel}>
      {error ? (
        <p className={styles.alert} role="alert">
          {"conflict" === error ? (
            <Trans>
              Someone else settled this intervention while it was open. Reopen
              it to see where it stands.
            </Trans>
          ) : (
            error
          )}
        </p>
      ) : null}

      <div className={styles.columns}>
        <Section icon="Ruler" legend={t`Estimated`}>
          <Reading label={t`Estimated price`}>
            {money(sheet.estimatedPrice)}
          </Reading>

          <Reading label={t`Estimated hours`}>
            {formatMinutes(sheet.estimatedMinutes)}
          </Reading>

          <Reading label={t`Estimated valued hours`}>
            {formatMinutes(sheet.estimatedValuedMinutes)}
          </Reading>
        </Section>

        <Section icon="CircleCheck" legend={t`Done`}>
          <Reading label={t`Actual price`}>{money(sheet.donePrice)}</Reading>

          <Reading label={t`Hours done`} delta={sheet.deltaMinutes}>
            {formatMinutes(sheet.validatedMinutes)}
          </Reading>

          <Reading
            label={t`Valued hours done`}
            delta={sheet.valuedDeltaMinutes}
          >
            {formatMinutes(sheet.validatedValuedMinutes)}
          </Reading>

          <Reading label={t`Expenses nobody is billed for`}>
            {money(sheet.expensesNonBilledTotal)}
          </Reading>

          <Field>
            <FieldLabel>{t`Actual hour price`}</FieldLabel>

            <FieldContent>
              <span className={styles.amountField}>
                <Input
                  className={styles.amountInput}
                  value={state.hourPrice}
                  disabled={sheet.locked}
                  onChange={(event) =>
                    setState((draft) => {
                      draft.hourPrice = event.target.value;
                    })
                  }
                />
                CHF
              </span>
            </FieldContent>
          </Field>
        </Section>

        {sheet.billable ? (
          <Section icon="ReceiptText" legend={t`To bill`}>
            <Field>
              <FieldLabel>{t`Final price to bill`}</FieldLabel>

              <FieldContent>
                <span className={styles.amountField}>
                  <Input
                    className={styles.amountInput}
                    value={state.price}
                    disabled={sheet.locked}
                    onChange={(event) =>
                      setState((draft) => {
                        draft.price = event.target.value;
                      })
                    }
                  />
                  CHF
                </span>

                {null === gain ? null : (
                  <span className={gain < 0 ? styles.loss : styles.win}>
                    {gain < 0 ? (
                      <Trans>Loss: {money(sheet.bill?.gain ?? null)}</Trans>
                    ) : (
                      <Trans>Gain: {money(sheet.bill?.gain ?? null)}</Trans>
                    )}
                  </span>
                )}
              </FieldContent>
            </Field>

            <Reading label={t`Expenses to bill`}>
              {money(sheet.expensesBilledTotal)}
            </Reading>

            {sheet.bill?.id ? (
              <>
                <Reading label={t`Bill number`}>
                  {sheet.bill.number ?? "-"}
                </Reading>

                <Reading label={t`Billed by`}>
                  {sheet.bill.billedByLabel ?? "-"}
                </Reading>

                <Reading label={t`Billing date`}>
                  {sheet.bill.billedDate ?? "-"}
                </Reading>
              </>
            ) : null}
          </Section>
        ) : null}
      </div>

      <footer className={styles.actions}>
        {sheet.billable ? (
          <Button
            variant="primary"
            loading={saving}
            disabled={sheet.locked}
            onClick={() => void apply("save")}
          >
            <Trans>Validate</Trans>
          </Button>
        ) : null}

        {sheet.canSendToBilling ? (
          <Button
            variant="secondary"
            loading={saving}
            onClick={() => void apply("send")}
          >
            <Trans>Send to billing</Trans>
          </Button>
        ) : null}

        {sheet.canClose ? (
          <Button
            variant="primary"
            loading={saving}
            onClick={() => void apply("close")}
          >
            <Trans>Close</Trans>
          </Button>
        ) : null}
      </footer>
    </div>
  );
}

/**
 * A figure the sheet computed, in the shape of a field so it lines up with the
 * two that are typed. The delta is what it came out at against the estimate.
 */
function Reading({
  label,
  delta,
  children,
}: {
  label: string;
  delta?: number;
  children: ReactNode;
}): JSX.Element {
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>

      <FieldContent>
        <span className={styles.reading}>
          <span>{children}</span>

          {undefined === delta || 0 === delta ? null : (
            <span className={delta < 0 ? styles.under : styles.over}>
              {delta < 0 ? "-" : "+"} {formatMinutes(Math.abs(delta))}
            </span>
          )}
        </span>
      </FieldContent>
    </Field>
  );
}
